import { ReactNode } from "react";

import StaticPage from "components/static-page";
import Wrapper from "components/wrapper";
import NotfoundIcon from "icons/notfound";
import { TaskState } from "types/types";

const { locales } = require("../lingui.config.js");

const API_URL = process.env.NEXT_PUBLIC_API_URL;
const API_VERSION = process.env.NEXT_PUBLIC_API_VERSION;

const taskStateObject = [
  {
    name: "unassigned",
    color: "#989898",
    tint: "#f8f8f8",
  },
  {
    name: "active",
    color: "#a5ff00",
    tint: "#effff0",
  },
  {
    name: "assigned",
    color: "#54c7fc",
    tint: "#edf9ff",
  },
  {
    name: "accepted",
    color: "#0068e9",
    tint: "#eff5ff",
  },
  {
    name: "failed",
    color: "#f9fe4a",
    tint: "#fff5e5",
  },
  {
    name: "transit",
    color: "#9b29ff",
    tint: "#f6edff",
  },
  {
    name: "completed",
    color: "#00B76A",
    tint: "#c8f8e4",
  },
  {
    name: "cancelled",
    color: "#ff2851",
    tint: "#ffe8ef",
  },
  {
    name: "recurrence",
    color: "#3B4049",
    tint: "#fff",
  },
];
const taskMarkerColors: any = {
  unassigned: "#ffffff",
  active: "#a5ff00",
  assigned: "#54c7fc",
  completed: "#00b76a",
  accepted: "#0068e9",
  failed: "#f9fe4a",
  transit: "#9b29ff",
  cancelled: "#ff2851",
};

const httpFetcher =
  (language: string = "en") =>
  async (url: string) => {
    if (!url.startsWith("https://")) {
      url = `${API_URL}/${url}`;
    }
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Accept: `application/json; version=${API_VERSION}`,
        "Accept-Language": language,
      },
    });
    if (res.status !== 200) {
      const error: any = new Error("An error occurred while fetching the data.");
      error.status = res.status;
      throw error;
    }
    return await res.json();
  };

const apiFetcher = (url: string) => fetch(url).then((r) => r.json());

const httpRequestHandler = async (path: string, method: string, body: object) => {
  const requestOptions = {
    method: method,
    headers: {
      Accept: `application/json; version=${API_VERSION}`,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : null,
  };
  return await fetch(`${API_URL}/${path}`, requestOptions).then((response) => {
    return response;
  });
};

const defaultNotFound = (msg = "") => {
  return (
    <Wrapper>
      <StaticPage
        icon={<NotfoundIcon />}
        title={"Nothing Found"}
        description={msg ? msg : "It looks like your link has expired or wasn’t copied correctly."}
      />
    </Wrapper>
  );
};

const getTaskState = (v: string) => {
  const state = taskStateObject.find((val) => val.name === v);
  if (state) {
    return state;
  }
  return null;
};

const assignment = `<path fill-rule="evenodd" transform="translate(11,10)"  clip-rule="evenodd" d="M13.807 2.4836L13.0264 2.29944L10.9165 4.39726L9.72148 3.1802L11.7793 1.02132L11.5991 0.22863C11.1643 0.083153 10.7094 0.00616729 10.2509 0.000430711C9.75076 -0.00726077 9.25436 0.0881354 8.79267 0.280675C8.34688 0.482461 7.94393 0.767859 7.60563 1.12141C7.22905 1.47813 6.93081 1.9093 6.72987 2.38751C6.34298 3.33923 6.34298 4.40439 6.72987 5.3561C4.64329 7.40888 2.58365 9.48888 0.551481 11.5955C0.341298 11.8708 0.24121 12.2141 0.267233 12.5594C0.287743 12.9308 0.454763 13.2788 0.731638 13.5272C0.855746 13.6623 1.00388 13.7744 1.16902 13.8555C1.33917 13.9306 1.52233 13.9736 1.70749 13.9826C2.02377 13.9766 2.32703 13.8565 2.56223 13.6453C4.11159 12.1871 7.08018 9.19144 8.76665 7.43591C9.22404 7.63008 9.71547 7.73017 10.2119 7.72917C10.7116 7.72878 11.2062 7.62822 11.6664 7.43345C12.1266 7.23868 12.5431 6.95364 12.8912 6.59518C13.6064 5.8658 14.0058 4.8843 14.0032 3.8628C14.0148 3.39537 13.9485 2.92927 13.807 2.4836ZM1.95671 12.9357C1.91818 12.9699 1.87175 12.994 1.82159 13.0058C1.77225 13.0143 1.7218 13.0143 1.67246 13.0058C1.62072 12.9979 1.57145 12.9784 1.52833 12.9487C1.48207 12.9216 1.44295 12.8839 1.41423 12.8386C1.27411 12.6955 1.143 12.4233 1.27411 12.2701C2.64531 10.8119 5.46877 7.96137 7.23431 6.21885C7.3344 6.35097 7.44449 6.47708 7.5626 6.59518C7.6807 6.71829 7.80781 6.83239 7.94293 6.93648C6.23544 8.68801 3.45101 11.5045 1.95771 12.9357H1.95671ZM13.0844 3.8628C13.0864 4.62847 12.7882 5.36411 12.2517 5.91259C11.7163 6.43774 10.9964 6.73192 10.2464 6.73192C9.49652 6.73192 8.77653 6.43774 8.24119 5.91259C7.84883 5.50312 7.5808 4.99067 7.46829 4.43484C7.35578 3.87902 7.40341 3.30267 7.60563 2.77285C7.74024 2.41227 7.94904 2.08395 8.21851 1.80914C8.48798 1.53432 8.81213 1.31912 9.17 1.17746C9.50979 1.03524 9.87458 0.962417 10.2429 0.96327H10.4972L8.67857 2.8379V3.53451L10.5752 5.42216H11.2358L13.0844 3.60457V3.8628ZM2.26397 5.68939H3.79631L4.50193 6.40702L5.18052 5.73243L4.51494 5.05384V5.0108L4.57199 3.36036L4.3518 2.92298L1.4893 1.03934L0.897783 1.10539L0.0660577 1.95514L0 2.55466L1.8396 5.4752L2.26397 5.69039V5.68939ZM1.31815 2.05422L3.63417 3.57955L3.59514 4.70353H2.5182L1.02489 2.33947L1.31815 2.05422ZM8.42935 9.0203L9.10394 8.34671L12.2127 11.5345C12.4745 11.8097 12.6205 12.175 12.6205 12.5549C12.6205 12.9347 12.4745 13.3001 12.2127 13.5753C12.0158 13.7774 11.7626 13.9156 11.4861 13.9718C11.2095 14.028 10.9225 13.9997 10.6623 13.8905C10.4962 13.8169 10.3473 13.7095 10.2249 13.5753L7.08017 10.3695L7.75977 9.69088L10.8945 12.8877C10.9367 12.9342 10.9891 12.9702 11.0476 12.9928C11.1628 13.0395 11.2917 13.0395 11.4069 12.9928C11.4655 12.9701 11.5179 12.9341 11.5601 12.8877C11.6039 12.8443 11.638 12.7921 11.6602 12.7345C11.6831 12.6758 11.6953 12.6134 11.6962 12.5504C11.6952 12.4874 11.6833 12.4251 11.6612 12.3662C11.639 12.3087 11.6049 12.2565 11.5611 12.2131L8.42935 9.0203Z" fill="white"/>`;
const dropOff = `<path fill-rule="evenodd" clip-rule="evenodd" d="M11.2 13.6V12H24.8V13.6H11.2ZM10 11.6C10 11.1582 10.3582 10.8 10.8 10.8H25.2C25.6418 10.8 26 11.1582 26 11.6V14C26 14.4419 25.6418 14.8 25.2 14.8H25V23.6C25 24.3732 24.3732 25 23.6 25H12.4C11.6268 25 11 24.3732 11 23.6V14.8H10.8C10.3582 14.8 10 14.4419 10 14V11.6ZM12.2 14.8V23.6C12.2 23.7105 12.2895 23.8 12.4 23.8H23.6C23.7105 23.8 23.8 23.7105 23.8 23.6V14.8H12.2ZM15.1757 19.3356C15.4101 19.1012 15.7899 19.1012 16.0243 19.3356L17.4001 20.7114L17.4001 16.4C17.4001 16.0687 17.6687 15.8 18.0001 15.8C18.3315 15.8 18.6001 16.0687 18.6001 16.4L18.6001 20.7112L19.9757 19.3356C20.2101 19.1012 20.5899 19.1012 20.8243 19.3356C21.0586 19.5699 21.0586 19.9498 20.8243 20.1841L18.4243 22.5841C18.1899 22.8184 17.8101 22.8184 17.5757 22.5841L15.1757 20.1841C14.9414 19.9498 14.9414 19.5699 15.1757 19.3356Z" fill="white"/>`;
const pickUp = `<path fill-rule="evenodd" transform="translate(10,10)" clip-rule="evenodd" d="M1.2 3.6V2H14.8V3.6H1.2ZM0 1.6C0 1.15818 0.358172 0.800003 0.8 0.800003H15.2C15.6418 0.800003 16 1.15818 16 1.6V4C16 4.44183 15.6418 4.8 15.2 4.8H15V13.6C15 14.3732 14.3732 15 13.6 15H2.4C1.6268 15 1 14.3732 1 13.6V4.8H0.8C0.358172 4.8 0 4.44183 0 4V1.6ZM2.2 4.8V13.6C2.2 13.7105 2.28954 13.8 2.4 13.8H13.6C13.7105 13.8 13.8 13.7105 13.8 13.6V4.8H2.2ZM10.8243 9.22427C10.5899 9.45858 10.2101 9.45858 9.97574 9.22427L8.5999 7.84843L8.5999 12.1598C8.5999 12.4911 8.33127 12.7598 7.9999 12.7598C7.66853 12.7598 7.3999 12.4911 7.3999 12.1598L7.3999 7.84863L6.02426 9.22427C5.78995 9.45858 5.41005 9.45858 5.17574 9.22427C4.94142 8.98995 4.94142 8.61005 5.17574 8.37574L7.57574 5.97574C7.81005 5.74142 8.18995 5.74142 8.42426 5.97574L10.8243 8.37574C11.0586 8.61005 11.0586 8.98995 10.8243 9.22427Z" fill="white"/>`;

const getCategoryIcon = (category: string) => {
  switch (category) {
    case "assignment":
      return assignment;
    case "pick_up":
      return pickUp;
    default:
      return dropOff;
  }
};

const getTaskMarkerWithCategory = (
  state: TaskState | undefined = TaskState.TRANSIT,
  category: string | undefined = "assignment",
  darkMode: boolean,
  index: number,
  isBlurred: boolean,
  overrideCategory: ReactNode = null,
  categoryColor: string = "#2A2F39"
) => {
  const idx = index.toString();
  const fontSize = idx.length > 1 ? 16 / idx.length + 5 : 12;
  const fill = taskMarkerColors[state];

  const svg = `<svg width="36" height="70" viewBox="0 0 36 70" fill="none" xmlns="http://www.w3.org/2000/svg" opacity="${
    isBlurred ? 0.3 : 1
  }">
<path fill-rule="evenodd" clip-rule="evenodd" d="M18 0C8.0595 0 0 8.14091 0 18.1818C0 32.9545 18 50 18 50C18 50 36 32.9545 36 18.1818C36 8.14091 27.9405 0 18 0Z" fill="${fill}"/>
<circle cx="18" cy="18" r="14" fill="${categoryColor}" />
${overrideCategory ? overrideCategory : getCategoryIcon(category)}
<circle cx="19" cy="62" r="8" fill="${darkMode ? "white" : "black"}" />
<text font-family="Helvetica" font-size="${fontSize}" text-anchor="middle" fill="${
    darkMode ? "black" : "white"
  }"><tspan x="18.5" y="66.5">${index}</tspan></text>
</svg>
`;
  return `data:image/svg+xml;charset=utf8,${encodeURIComponent(svg)}`;
};

const getLocale = (locale: string | undefined) => {
  return locales?.includes(locale) ? (locale as string) : "en";
};

const getBoundsFromLatLng = (lat: number, lng: number, distanceInKM: number = 1) => {
  const kmInDegreeLat = distanceInKM / 111.32; // 1 km in degrees of latitude

  const deltaLat = kmInDegreeLat; // ≈ 1 km in degrees

  const deltaLng = kmInDegreeLat / Math.cos((lat * Math.PI) / 180);

  return [
    { lat: lat - deltaLat, lng: lng - deltaLng },
    { lat: lat + deltaLat, lng: lng + deltaLng },
  ];
};

export {
  API_URL,
  API_VERSION,
  apiFetcher,
  getTaskMarkerWithCategory,
  getTaskState,
  httpFetcher,
  httpRequestHandler,
  defaultNotFound,
  getLocale,
  getBoundsFromLatLng,
};
